/**
 * SpecialTableEditPage Component
 * 
 * Página de criação/edição de Tabelas Especiais de Preços.
 * Permite configurar tabelas, vincular clientes, definir margens e adicionar produtos.
 * 
 * Funcionalidades:
 * - Configuração geral da tabela (nome, cliente, margem)
 * - Busca e adição de produtos
 * - Edição de preços individuais por produto
 * - Cálculo automático de preços com margem
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Search, Plus, Trash2, 
  Save, Percent, Package, Building2, 
  Loader2, Sparkles, Command, X, ArrowRight,
  Tag, User, Settings
} from 'lucide-react';
import { useToast } from '../../../../hooks/useToast';
import { Toast } from '../../../../components';
import { 
  specialPricingTablesService,
  productService,
  customerService
} from '../../../../services/erpApi';
import './SpecialTableEditPage.css';

const SpecialTableEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  
  const [tableName, setTableName] = useState('');
  const [description, setDescription] = useState('');
  const [clientId, setClientId] = useState(null);
  const [clientName, setClientName] = useState('');
  const [globalMargin, setGlobalMargin] = useState(30);
  const [isGeneral, setIsGeneral] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Product Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  
  // Client Search state
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [isSearchingClients, setIsSearchingClients] = useState(false);
  
  const searchRef = useRef(null);
  const clientRef = useRef(null);

  // Carregar dados se for edição
  const loadTable = useCallback(async () => {
    setLoading(true);
    try {
      const response = await specialPricingTablesService.getById(id);
      if (response.success && response.data) {
        const table = response.data;
        setTableName(table.name || '');
        setDescription(table.description || '');
        setClientId(table.client_id);
        setClientName(table.client_name || '');
        setGlobalMargin(parseFloat(table.global_margin) || 0);
        setIsGeneral(table.is_general || false);
        
        // Carregar itens da tabela
        if (table.items && table.items.length > 0) {
          setSelectedProducts(table.items.map(item => ({
            id: item.product_id,
            name: item.product_name,
            sku: item.product_sku,
            price: parseFloat(item.product_base_price || 0),
            cost_price: parseFloat(item.product_cost_price || 0),
            image_url: item.product_image_url,
            customPrice: item.custom_price ? parseFloat(item.custom_price) : undefined,
            marginOverride: item.margin_override ? parseFloat(item.margin_override) : undefined
          })));
        }
      } else {
        showToast('Tabela não encontrada', 'error');
        navigate('/erp/vendas/tabelas-especiais');
      }
    } catch (error) {
      console.error('Erro ao carregar tabela:', error);
      showToast('Erro ao carregar tabela', 'error');
      navigate('/erp/vendas/tabelas-especiais');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, showToast]);

  useEffect(() => {
    if (id) {
      loadTable();
    }
  }, [id, loadTable]);

  // Fechar dropdowns ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
      if (clientRef.current && !clientRef.current.contains(e.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Busca de produtos
  useEffect(() => {
    const trimmedTerm = searchTerm.trim();
    
    if (trimmedTerm.length >= 2) {
      setIsSearching(true);
      setShowDropdown(true);
      
      const timeoutId = setTimeout(async () => {
        try {
          const response = await productService.getAll({ 
            search: trimmedTerm,
            active: true,
            limit: 8
          });
          if (response.success) {
            const filtered = (response.data || [])
              .filter(product => {
                const searchLower = trimmedTerm.toLowerCase();
                return (
                  product.name?.toLowerCase().includes(searchLower) ||
                  product.sku?.toLowerCase().includes(searchLower) ||
                  product.barcode?.toLowerCase().includes(searchLower)
                );
              })
              .slice(0, 8);
            setSearchResults(filtered);
          }
        } catch (error) {
          console.error('Erro ao buscar produtos:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 300);

      return () => clearTimeout(timeoutId);
    } else {
      setSearchResults([]);
      setShowDropdown(false);
      setIsSearching(false);
    }
  }, [searchTerm]);

  // Busca de clientes
  useEffect(() => {
    const trimmedTerm = clientSearch.trim();
    
    if (trimmedTerm.length >= 2) {
      setIsSearchingClients(true);
      setShowClientDropdown(true);
      
      const timeoutId = setTimeout(async () => {
        try {
          const response = await customerService.getAll({ 
            search: trimmedTerm,
            limit: 5
          });
          if (response.success) {
            setClientSearchResults(response.data || []);
          }
        } catch (error) {
          console.error('Erro ao buscar clientes:', error);
          setClientSearchResults([]);
        } finally {
          setIsSearchingClients(false);
        }
      }, 300);

      return () => clearTimeout(timeoutId);
    } else {
      setClientSearchResults([]);
      setShowClientDropdown(false);
      setIsSearchingClients(false);
    }
  }, [clientSearch]);

  const handleAddProduct = useCallback((product) => {
    if (!selectedProducts.find(p => p.id === product.id)) {
      setSelectedProducts([...selectedProducts, { 
        ...product,
        price: parseFloat(product.price || 0),
        cost_price: parseFloat(product.cost_price || product.costPrice || 0)
      }]);
    }
    setSearchTerm('');
    setShowDropdown(false);
  }, [selectedProducts]);

  const handleRemoveProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
  };

  const handleUpdateCustomPrice = (productId, price) => {
    const val = parseFloat(price);
    setSelectedProducts(selectedProducts.map(p => 
      p.id === productId ? { ...p, customPrice: isNaN(val) ? undefined : val } : p
    ));
  };

  const calculateAdjustedPrice = (product) => {
    const cost = parseFloat(product.cost_price ?? product.costPrice ?? 0);
    const margin = product.marginOverride ?? globalMargin;

    if (cost > 0 && margin < 100) {
      const price = cost / (1 - margin / 100);
      return Math.round(price * 100) / 100;
    }

    return parseFloat(product.price || 0);
  };

  const handleSelectClient = (client) => {
    setClientId(client.id);
    setClientName(client.name);
    setClientSearch(client.name);
    setShowClientDropdown(false);
    setIsGeneral(false);
  };

  const handleSetGeneral = () => {
    setClientId(null);
    setClientName('Vários Clientes (Geral)');
    setClientSearch('');
    setShowClientDropdown(false);
    setIsGeneral(true);
  };

  const handleSave = async () => {
    if (!tableName.trim()) {
      showToast('Nome da tabela é obrigatório', 'warning');
      return;
    }

    setIsSaving(true);
    try {
      const tableData = {
        name: tableName.trim(),
        description: description.trim() || null,
        clientId: isGeneral ? null : clientId,
        clientName: isGeneral ? 'Vários Clientes (Geral)' : clientName,
        globalMargin: globalMargin,
        isGeneral: isGeneral
      };

      let response;
      if (id) {
        response = await specialPricingTablesService.update(id, tableData);
      } else {
        response = await specialPricingTablesService.create(tableData);
      }

      if (response.success) {
        const tableId = response.data.id || id;
        
        // Adicionar/atualizar produtos
        for (const product of selectedProducts) {
          try {
            await specialPricingTablesService.addItem(tableId, {
              productId: product.id,
              customPrice: product.customPrice || null,
              marginOverride: product.marginOverride || null
            });
          } catch (error) {
            console.error(`Erro ao adicionar produto ${product.id}:`, error);
          }
        }

        showToast(id ? 'Tabela atualizada com sucesso!' : 'Tabela criada com sucesso!', 'success');
        navigate('/erp/vendas/tabelas-especiais');
      } else {
        showToast(response.message || 'Erro ao salvar tabela', 'error');
      }
    } catch (error) {
      console.error('Erro ao salvar tabela:', error);
      showToast('Erro ao salvar tabela', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const formatCurrency = (val) => 
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);

  if (loading) {
    return (
      <div className="special-table-edit-page loading">
        <Loader2 className="spinner" size={32} />
        <p>Carregando tabela...</p>
      </div>
    );
  }

  return (
    <div className="special-table-edit-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <button 
            className="btn-back"
            onClick={() => navigate('/erp/vendas/tabelas-especiais')}
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1>Configurar <span className="highlight">Tabela</span></h1>
            <p className="subtitle">Personalização de precificação estratégica</p>
          </div>
        </div>
        <div className="header-actions">
          <button 
            className="btn-primary btn-save"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="spinner-icon" size={18} />
                Salvando...
              </>
            ) : (
              <>
                <Save size={18} />
                Salvar Tabela
              </>
            )}
          </button>
        </div>
      </div>

      <div className="page-content-grid">
        {/* Settings Column */}
        <div className="settings-column">
          <div className="settings-card">
            <div className="card-background-decoration"></div>
            
            <h3 className="section-title">
              <Settings size={16} />
              Definições Gerais
            </h3>
            
            <div className="settings-form">
              {/* Nome da Tabela */}
              <div className="form-group">
                <label>Identificação da Tabela</label>
                <input 
                  type="text" 
                  value={tableName}
                  onChange={(e) => setTableName(e.target.value)}
                  className="form-control"
                  placeholder="Ex: Tabela Regional Sul"
                />
              </div>

              {/* Descrição */}
              <div className="form-group">
                <label>Descrição (Opcional)</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="form-control"
                  rows={3}
                  placeholder="Descrição da tabela..."
                />
              </div>

              {/* Seletor de Cliente */}
              <div className={`form-group ${showClientDropdown ? 'has-dropdown' : ''}`} ref={clientRef}>
                <label>Vincular a Cliente (Opcional)</label>
                <div className="input-wrapper">
                  <div className="input-icon">
                    <Building2 size={18} />
                  </div>
                  <input 
                    type="text" 
                    value={clientName || clientSearch}
                    onChange={(e) => {
                      setClientSearch(e.target.value);
                      if (clientName) {
                        setClientName('');
                        setClientId(null);
                        setIsGeneral(false);
                      }
                      setShowClientDropdown(true);
                    }}
                    onFocus={() => setShowClientDropdown(true)}
                    className={`form-control input-with-icon ${clientName ? 'has-value' : ''}`}
                    placeholder="Pesquisar Hospital ou Clínica..."
                  />
                  {clientName && (
                    <button 
                      className="btn-clear-input"
                      onClick={() => {
                        setClientName('');
                        setClientId(null);
                        setClientSearch('');
                        setIsGeneral(false);
                      }}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>

                {/* Dropdown de Clientes */}
                {showClientDropdown && (
                  <div className="dropdown-menu client-dropdown">
                    <div className="dropdown-content">
                      {isSearchingClients ? (
                        <div className="dropdown-loading">
                          <Loader2 size={20} />
                          <span>Buscando clientes...</span>
                        </div>
                      ) : clientSearchResults.length > 0 ? (
                        clientSearchResults.map(client => (
                          <button
                            key={client.id}
                            className="dropdown-item"
                            onClick={() => handleSelectClient(client)}
                          >
                            <div className="dropdown-item-content">
                              <div className="dropdown-item-icon">
                                <User size={14} />
                              </div>
                              <span>{client.name}</span>
                            </div>
                            <ArrowRight size={14} />
                          </button>
                        ))
                      ) : clientSearch.trim().length >= 2 ? (
                        <div className="dropdown-empty">
                          <User size={24} />
                          <span>Nenhum cliente encontrado</span>
                        </div>
                      ) : null}
                      {clientSearch.trim().length === 0 && (
                        <button 
                          className="dropdown-item dropdown-item-general"
                          onClick={handleSetGeneral}
                        >
                          Usar como Tabela Geral
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Margem Global */}
              <div className="form-group margin-group">
                <label className="margin-label">
                  Margem Padrão (%)
                  <span className="margin-value">{globalMargin}%</span>
                </label>
                <div className="input-wrapper">
                  <div className="input-icon">
                    <Percent size={18} />
                  </div>
                  <input 
                    type="range" 
                    min="0" 
                    max="1000" 
                    step="1"
                    value={globalMargin}
                    onChange={(e) => setGlobalMargin(parseInt(e.target.value))}
                    className="margin-slider"
                  />
                </div>
                <p className="form-help">
                  Esta margem será aplicada automaticamente a todos os produtos vinculados.
                </p>
              </div>
            </div>
          </div>

          <div className="ai-card">
            <div className="card-background-decoration"></div>
            <div className="ai-content">
              <div className="ai-header">
                <Sparkles size={20} />
                <span>IA de Preçificação</span>
              </div>
              <p className="ai-description">
                O Gemini analisará o perfil de compra do cliente <strong>{clientName || 'selecionado'}</strong> para sugerir a margem ideal.
              </p>
              <button 
                className="btn-ai"
                disabled={!clientName && !isGeneral}
              >
                Sugerir Margem IA
              </button>
            </div>
          </div>
        </div>

        {/* Products Canvas */}
        <div className="products-column">
          <div className="products-card">
            {/* Product Search Input */}
            <div className="products-search-section" ref={searchRef}>
              <h3 className="section-title">Vincular Produtos ao Catálogo</h3>
              <div className="search-wrapper">
                <div className="input-icon">
                  <Search size={20} />
                </div>
                <input 
                  type="text" 
                  placeholder="Pesquisar por nome ou SKU para adicionar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => searchTerm.length >= 2 && setShowDropdown(true)}
                  className="search-input"
                />
                <div className="search-shortcut">
                  <Command size={10} />
                  K
                </div>

                {/* SEARCH DROPDOWN */}
                {showDropdown && (
                  <div className="dropdown-menu search-dropdown">
                    {isSearching ? (
                      <div className="dropdown-loading">
                        <Loader2 size={32} />
                        <span>Varrendo Catálogo Medik...</span>
                      </div>
                    ) : searchResults.length > 0 ? (
                      <div className="dropdown-content">
                        {searchResults.map(product => (
                          <button 
                            key={product.id}
                            className="dropdown-item product-item"
                            onClick={() => handleAddProduct(product)}
                          >
                            <div className="product-item-image">
                              {product.image_url ? (
                                <img src={product.image_url} alt={product.name} />
                              ) : (
                                <Package size={24} />
                              )}
                            </div>
                            <div className="product-item-content">
                              <p className="product-item-sku">{product.sku}</p>
                              <p className="product-item-name">{product.name}</p>
                            </div>
                            <div className="product-item-price">
                              <p>{formatCurrency(product.price || 0)}</p>
                              <Plus size={16} />
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="dropdown-empty">
                        <Package size={48} />
                        <span>Nenhum produto encontrado</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Added Products Table */}
            <div className="products-table-wrapper">
              <table className="products-table">
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th className="text-right">Preço Base</th>
                    <th className="text-right">Preço c/ Margem</th>
                    <th className="text-right">Preço Individual</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {selectedProducts.length > 0 ? selectedProducts.map((product) => (
                    <tr key={product.id} className="product-row">
                      <td>
                        <div className="product-cell">
                          <div className="product-image">
                            {product.image_url ? (
                              <img src={product.image_url} alt={product.name} />
                            ) : (
                              <Package size={16} />
                            )}
                          </div>
                          <div className="product-info">
                            <p className="product-name">{product.name}</p>
                            <p className="product-sku">{product.sku}</p>
                          </div>
                        </div>
                      </td>
                      <td className="text-right">
                        <span className="price-base">{formatCurrency(product.price)}</span>
                      </td>
                      <td className="text-right">
                        <span className="price-margin">{formatCurrency(calculateAdjustedPrice(product))}</span>
                      </td>
                      <td className="text-right">
                        <div className="price-input-wrapper">
                          <span className="price-currency">R$</span>
                          <input 
                            type="number" 
                            placeholder="Manual"
                            value={product.customPrice || ''}
                            onChange={(e) => handleUpdateCustomPrice(product.id, e.target.value)}
                            className="price-input"
                            step="0.01"
                            min="0"
                          />
                        </div>
                      </td>
                      <td className="text-right">
                        <button 
                          className="btn-icon-danger"
                          onClick={() => handleRemoveProduct(product.id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={5} className="empty-table">
                        <div className="empty-table-content">
                          <Tag size={48} />
                          <p>Nenhum item adicionado à tabela</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          show={toast.show}
          onClose={hideToast}
        />
      )}
    </div>
  );
};

export default SpecialTableEditPage;
